import { Connector, ConnectorCreate, ConnectorUpdate, NextScheduledRun, Run } from '@/types';
import { apiClient } from './client';

/**
 * Validate connector type
 */
function validateConnectorType(type: string): void {
  const validTypes = ['sharepoint', 'onedrive', 'blob'];
  if (!validTypes.includes(type)) {
    throw new Error(
      `Invalid connector type: ${type}. Must be one of: ${validTypes.join(', ')}`
    );
  }
}

/**
 * Connectors API service
 */
export const connectorsApi = {
  /**
   * List all connectors
   */
  async list(): Promise<Connector[]> {
    const { data } = await apiClient.get<Connector[]>('/connectors');
    return data;
  },

  /**
   * Get a single connector by ID
   */
  async get(id: string): Promise<Connector> {
    const { data } = await apiClient.get<Connector>(`/connectors/${id}`);
    return data;
  },

  /**
   * Create a new connector
   */
  async create(payload: ConnectorCreate): Promise<Connector> {
    // Validate connector type
    validateConnectorType(payload.type);

    const { data } = await apiClient.post<Connector>('/connectors', payload);
    return data;
  },

  /**
   * Update an existing connector
   */
  async update(id: string, payload: ConnectorUpdate): Promise<Connector> {
    const { data } = await apiClient.put<Connector>(
      `/connectors/${id}`,
      payload
    );
    return data;
  },

  /**
   * Delete a connector
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/connectors/${id}`);
  },

  /**
   * Get connector statistics
   */
  async getStats(): Promise<{
    totalConnectors: number;
    activeConnectors: number;
    totalDocumentsProcessed: number;
  }> {
    const { data } = await apiClient.get('/connectors/stats');
    return data;
  },

  /**
   * Test connector configuration
   */
  async test(id: string): Promise<{ success: boolean; message?: string }> {
    const { data } = await apiClient.post(`/connectors/${id}/test`);
    return data;
  },

  /**
   * Get connector run history
   */
  async getRunHistory(id: string, limit = 10): Promise<Run[]> {
    const { data } = await apiClient.get(`/connectors/${id}/runs`, {
      params: { limit },
    });
    return data;
  },

  /**
   * Check if a sync token exists for a connector
   */
  async checkSyncToken(id: string): Promise<{ hasSyncToken: boolean; connectorId: string }> {
    const { data } = await apiClient.get(`/connectors/${id}/sync-token`);
    return data;
  },

  /**
   * Clear sync token for a connector to force full resync on next run
   */
  async clearSyncToken(id: string): Promise<void> {
    await apiClient.delete(`/connectors/${id}/sync-token`);
  },

  /**
   * Get the next scheduled run time for a connector
   */
  async getNextRun(id: string): Promise<NextScheduledRun> {
    const { data } = await apiClient.get<NextScheduledRun>(`/connectors/${id}/next-run`);
    return data;
  },
};
